import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ImportReview() {
  const [tab, setTab] = useState("wo");
  const [tenants, setTenants] = useState([]);
  const [wos, setWos] = useState([]);
  const [selT, setSelT] = useState({});
  const [selW, setSelW] = useState({});
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [rT, rW] = await Promise.all([
        api.get("/import/tenants"),
        api.get("/import/work-orders"),
      ]);
      setTenants(rT.data.rows || []);
      setWos(rW.data.rows || []);
    } catch (e) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const filtT = tenants.filter(r => !q || (r.property_address + r.name + r.unit).toLowerCase().includes(q.toLowerCase()));
  const filtW = wos.filter(r => !q || (r.property_address + r.description + r.work_order_number + (r.vendor || "")).toLowerCase().includes(q.toLowerCase()));

  const toggleAll = (list, sel, setSel) => {
    const allOn = list.every(r => sel[r.id]);
    if (allOn) setSel({});
    else setSel(Object.fromEntries(list.map(r => [r.id, true])));
  };

  const promote = async (kind) => {
    const sel = kind === "wo" ? selW : selT;
    const ids = Object.keys(sel).filter(k => sel[k]);
    if (!ids.length) { toast.error("Select at least one row"); return; }
    if (!window.confirm(`Silently promote ${ids.length} ${kind === "wo" ? "work order(s)" : "tenant(s)"}? No emails, no notifications will fire.`)) return;
    setBusy(true);
    try {
      const url = kind === "wo" ? "/import/work-orders/promote" : "/import/tenants/promote";
      const r = await api.post(url, { ids });
      toast.success(`Promoted ${r.data.promoted} silently — no notifications`);
      if (kind === "wo") setSelW({}); else setSelT({});
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Promote failed"));
    } finally { setBusy(false); }
  };

  const promoteToSweep = async () => {
    const ids = Object.keys(selW).filter(k => selW[k]);
    if (!ids.length) { toast.error("Select at least one work order"); return; }
    const kind = window.prompt(
      `Turn ${ids.length} imported WO(s) into empty inspection sweeps?\n\n` +
      `Type one: move_out (default), move_in, or periodic.\n` +
      `Silent — no SMS/email fires. Sweeps come pre-seeded with Bedroom/Bathroom/Kitchen/Living Room; techs can edit on-site.`,
      "move_out",
    );
    if (!kind) return;
    const norm = kind.trim().toLowerCase();
    if (!["move_out", "move_in", "periodic"].includes(norm)) {
      toast.error("Type must be move_out, move_in, or periodic");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/import/work-orders/promote-to-sweep",
        { ids, inspection_type: norm });
      toast.success(`${r.data.promoted} sweep(s) created silently — ready for the field team`);
      setSelW({});
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Sweep promote failed"));
    } finally { setBusy(false); }
  };

  const deleteRow = async (kind, id) => {
    if (!window.confirm("Delete this imported row (does not affect promoted records)?")) return;
    try {
      const url = kind === "wo" ? `/import/work-orders/${id}` : `/import/tenants/${id}`;
      await api.delete(url);
      await load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const list = tab === "wo" ? filtW : filtT;
  const sel = tab === "wo" ? selW : selT;
  const setSel = tab === "wo" ? setSelW : setSelT;

  return (
    <AdminShell title="Silent Import Review">
      <div className="p-6 space-y-4">
        <div className="p-3 bg-amber-50 border-2 border-amber-500 text-sm" data-testid="import-silent-banner">
          <b className="uppercase font-black text-xs tracking-widest text-amber-800">🔒 Silent Mode</b>
          <div className="text-slate-700 text-xs mt-1">
            Promoting a row here does <b>NOT</b> send emails, SMS, landlord alerts, or tenant portal invites.
            The record is added to your live database silently for internal tracking only.
          </div>
        </div>

        <div className="flex gap-2 flex-wrap border-b-2 border-slate-200 pb-2">
          <button onClick={() => setTab("wo")} data-testid="import-tab-wo"
            className={`h-11 px-4 border-2 font-black uppercase text-xs tracking-widest ${tab === "wo" ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white border-slate-300"}`}>
            Work Orders <span className="ml-1 px-1.5 bg-white/20 text-[10px]">{wos.length}</span>
          </button>
          <button onClick={() => setTab("tenants")} data-testid="import-tab-tenants"
            className={`h-11 px-4 border-2 font-black uppercase text-xs tracking-widest ${tab === "tenants" ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white border-slate-300"}`}>
            Tenants <span className="ml-1 px-1.5 bg-white/20 text-[10px]">{tenants.length}</span>
          </button>
          <div className="flex-1"/>
          <input type="text" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Filter…" data-testid="import-search"
            className="h-11 w-48 px-3 border-2 border-slate-300 text-sm"/>
          <button onClick={() => promote(tab)} disabled={busy || Object.keys(sel).filter(k => sel[k]).length === 0}
            data-testid={`import-promote-${tab}`}
            className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
            {busy ? "…" : `Promote ${Object.keys(sel).filter(k => sel[k]).length || "0"} (silent)`}
          </button>
          {tab === "wo" && (
            <button onClick={promoteToSweep} disabled={busy || Object.keys(selW).filter(k => selW[k]).length === 0}
              data-testid="import-promote-to-sweep"
              title="Turn selected WOs into empty inspection sweeps"
              className="h-11 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
              {busy ? "…" : `→ Sweep (${Object.keys(selW).filter(k => selW[k]).length || 0})`}
            </button>
          )}
        </div>

        <div className="border-2 border-slate-200 max-h-[70vh] overflow-auto">
          <table className="w-full text-xs" data-testid={`import-table-${tab}`}>
            <thead className="sticky top-0 bg-slate-50 border-b-2 border-slate-200 z-10">
              <tr>
                <th className="p-2 w-10 text-left">
                  <input type="checkbox" onChange={() => toggleAll(list, sel, setSel)}
                    checked={list.length > 0 && list.every(r => sel[r.id])}/>
                </th>
                {tab === "wo" ? (
                  <>
                    <th className="p-2 text-left font-black uppercase text-[10px] tracking-widest">WO #</th>
                    <th className="p-2 text-left font-black uppercase text-[10px] tracking-widest">Property</th>
                    <th className="p-2 text-left font-black uppercase text-[10px] tracking-widest">Description</th>
                    <th className="p-2 text-left font-black uppercase text-[10px] tracking-widest">Vendor</th>
                    <th className="p-2 text-left font-black uppercase text-[10px] tracking-widest">Status</th>
                  </>
                ) : (
                  <>
                    <th className="p-2 text-left font-black uppercase text-[10px] tracking-widest">Property</th>
                    <th className="p-2 text-left font-black uppercase text-[10px] tracking-widest">Unit</th>
                    <th className="p-2 text-left font-black uppercase text-[10px] tracking-widest">Name</th>
                    <th className="p-2 text-left font-black uppercase text-[10px] tracking-widest">Move-In</th>
                    <th className="p-2 text-left font-black uppercase text-[10px] tracking-widest">Rent</th>
                  </>
                )}
                <th className="p-2 w-16 text-left"/>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-400 font-black uppercase text-xs tracking-widest">No rows</td></tr>
              ) : list.map(r => (
                <tr key={r.id} className={`border-b border-slate-100 ${r.promoted_at ? "bg-emerald-50" : "hover:bg-slate-50"}`}
                  data-testid={`import-row-${r.id}`}>
                  <td className="p-2">
                    <input type="checkbox" checked={!!sel[r.id]}
                      onChange={() => setSel({ ...sel, [r.id]: !sel[r.id] })}
                      disabled={!!r.promoted_at}
                      data-testid={`import-check-${r.id}`}/>
                  </td>
                  {tab === "wo" ? (
                    <>
                      <td className="p-2 font-black">{r.work_order_number}</td>
                      <td className="p-2">{r.property_address} {r.unit && <span className="text-slate-400">· {r.unit}</span>}</td>
                      <td className="p-2 max-w-md truncate" title={r.description}>{r.description}</td>
                      <td className="p-2 text-slate-500 truncate max-w-[180px]">{r.vendor}</td>
                      <td className="p-2">
                        <span className="px-1.5 py-0.5 text-[10px] font-black uppercase bg-slate-200">{r.status}</span>
                        {r.promoted_at && <span className="ml-1 px-1.5 py-0.5 text-[10px] font-black uppercase bg-emerald-600 text-white">✓ Live</span>}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-2 truncate max-w-[280px]">{r.property_address}</td>
                      <td className="p-2">{r.unit}</td>
                      <td className="p-2 font-black">{r.name}</td>
                      <td className="p-2 text-slate-500">{r.move_in_date?.slice(0, 10) || "—"}</td>
                      <td className="p-2 text-emerald-700 font-black">${r.rent || 0}</td>
                    </>
                  )}
                  <td className="p-2 text-right">
                    <button onClick={() => deleteRow(tab, r.id)} title="Delete import row"
                      data-testid={`import-del-${r.id}`}
                      className="h-7 w-7 border border-red-300 text-red-500 hover:bg-red-50 font-black text-xs">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminShell>
  );
}
